import { type AppButtonProps } from '@/components/atoms';

import { usePageWhatsAppMessage } from './usePageWhatsAppMessage';

/**
 * The page's one primary action. One per screen (IA §5), so this is a single
 * value and not a list.
 *
 * `to` is a site path, or a `#id` on this page. The two are not the same
 * button: a path is a client-side route change, while a fragment has to stay a
 * real anchor so the browser does its own scrolling — the router does not
 * scroll for a hash, and re-implementing that in JavaScript would break on a
 * prerendered page whose script has not arrived yet. Whatever a `#id` points
 * at needs a `scrollMarginTop`, or the sticky header lands on top of it.
 *
 * The distinction now decides more than that. See `onPhone` below.
 */
export type PageIntroAction = { label: string; to: string };

type Input = {
  action?: PageIntroAction | undefined;
  /** Offer WhatsApp beside it, prefilled with the route's own message. */
  whatsapp?: boolean | undefined;
  /** The band is wearing its stage, which it does from `md` up. */
  stage?: boolean | undefined;
};

export type PageIntroActionsPlan = {
  /** The page asked for neither, so there is no row to draw at any width. */
  empty: boolean;
  /**
   * Whether the row is drawn below `md` as well, where the band is a title
   * block and the page's content starts immediately under it.
   *
   * Three outcomes, and only one of them survives a phone:
   *
   * - **A route action does.** Six pages (`/services/:slug`) point their
   *   button at another page — the tax calculator, the drive-home calculator,
   *   /cars, the import guide — and that is a path a visitor has no other way
   *   to find from the middle of a service page. Taking it away on phones
   *   would be removing navigation, not removing decoration.
   * - **An anchor action does not.** The rest of them (`#results`,
   *   `#request`, `#catalogue`) scroll down this same page. Below `md` the
   *   thing they scroll to is the next block on screen, so the button is a
   *   full-width invitation to move 200px — it costs more height than the
   *   scroll it saves.
   * - **WhatsApp does not.** `StickyActionBar` pins WhatsApp and the free-
   *   advice CTA to the bottom of every phone viewport (§60 L), so a second
   *   copy in the band is the same affordance twice on one screen.
   */
  onPhone: boolean;
  /** Null when the page has no primary action. */
  action: (PageIntroAction & { link: Pick<AppButtonProps, 'to' | 'href'> }) | null;
  /** The message the route's own handle supplies, or null when not offered. */
  whatsappMessage: string | null;
  /**
   * Whether WhatsApp steps down to an outline.
   *
   * Rank, when the page offers both: one filled button, not two. A solid red
   * next to a solid green is two primaries, and IA §5 allows one — the eye has
   * nowhere to land and the band's actual conversion path stops looking like
   * the conversion path.
   *
   * It only steps down on the stage. The green is legible as text there and is
   * not on the site's light surfaces, so a plain band keeps the filled button,
   * which carries its own contrast (see WhatsAppButton). That is also the only
   * case where stepping down would be wrong anyway: with no red button beside
   * it there is no second primary to resolve.
   */
  stepDown: boolean;
};

const linkFor = (action: PageIntroAction): Pick<AppButtonProps, 'to' | 'href'> =>
  action.to.startsWith('#') ? { href: action.to } : { to: action.to };

/**
 * Everything the opening band's call to action has to decide before it can be
 * drawn: whether there is a row at all, whether a phone sees it, which link
 * shape the button takes, what WhatsApp says, and which of the two is filled.
 *
 * All five used to be inline in `PageIntroActions`, which meant the component
 * that drew the row also owned the policy about which widths deserve one — and
 * meant `PageIntroCopy`, which owns the gap above the row, could not know
 * whether that gap was about to sit over anything. It reserved 56px on every
 * phone whether or not a button followed.
 *
 * The WhatsApp message is read from the route rather than passed in, so this
 * button and the floating one say the same thing on a page that only states it
 * once. Give a page a second copy of the message and the two drift.
 */
export function usePageIntroActions({ action, whatsapp, stage }: Input): PageIntroActionsPlan {
  const message = usePageWhatsAppMessage();
  const isRoute = !!action && !action.to.startsWith('#');

  return {
    empty: !action && !whatsapp,
    onPhone: isRoute,
    action: action ? { ...action, link: linkFor(action) } : null,
    whatsappMessage: whatsapp ? message : null,
    stepDown: !!stage && !!action,
  };
}
